import { useState } from "react";
import {
  useMutation,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";

import ProductList from "@/components/checkout/product-list";
import FluidImage from "@/components/images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { CartResponse } from "@/networking/carts.api";
import PricingSection from "./pricing-section";
import DiscountCodeInput from "./discount-code-input";
import { applyDiscount, removeDiscount } from "@/networking/discounts.api";
import DropZone from "@/components/drop-zones/drop-zone";
import { ApiError, isApiError } from "@/lib/api-helpers";

function ProductLoader() {
  return (
    <div className="py-6">
      <div className="animate-pulse">
        <div className="flex flex-row items-center justify-between space-x-4">
          <div className="flex flex-row space-x-4">
            <div className="h-16 w-16 rounded bg-gray-200"></div>
            <div className="space-y-3">
              <div className="h-3 w-24 rounded bg-gray-200"></div>
              <div className="h-3 w-14 rounded bg-gray-200"></div>
              <div className="h-3 w-14 rounded bg-gray-200"></div>
            </div>
          </div>
          <div className="h-7 w-20 rounded-full bg-gray-200"></div>
        </div>
      </div>
    </div>
  );
}

export default function CartView({
  cartToken,
  query,
  paymentMethodsIncludes,
  scrollRef,
  inFrame = false,
  isSubscriptionPending = false,
  isQuantityPending = false,
  isContactInfoPending = false,
}: {
  cartToken: string;
  query: UseQueryResult<CartResponse | null>;
  paymentMethodsIncludes: string | null;
  scrollRef?: React.RefObject<HTMLElement | null>;
  inFrame?: boolean;
  isSubscriptionPending?: boolean;
  isQuantityPending?: boolean;
  isContactInfoPending?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { subdomain } = query.data?.cart?.company || {};
  const [showSummary, setShowSummary] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState<string | undefined>(
    undefined,
  );
  const { cart } = query.data || {};
  const applyDiscountMutation = useMutation({
    mutationKey: ["apply-discount", cartToken],
    mutationFn: () => applyDiscount(discountCode, cartToken),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ["cart", cartToken, paymentMethodsIncludes],
      });
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);
      return { previousCart };
    },
    onSuccess: (response) => {
      setDiscountError(undefined);
      setDiscountCode("");
      queryClient.setQueryData(
        ["cart", cartToken, paymentMethodsIncludes],
        response,
      );
    },
    onError: (error: unknown, _variables, context) => {
      console.error("Error applying discount:", error);
      if (isApiError(error)) {
        const apiError = error as ApiError;
        const discountErrors =
          apiError?.data?.errors?.discount_code ??
          apiError?.data?.errors?.coupon_code;
        if (discountErrors?.[0] === "enter_email_to_use_this_code") {
          setDiscountError(t("enter_email_to_use_this_code"));
        } else {
          setDiscountError(t("invalid_discount_code"));
        }
      } else {
        setDiscountError(t("invalid_discount_code"));
      }
      setTimeout(() => {
        setDiscountError(undefined);
      }, 2500);
      setDiscountCode("");
      if (context?.previousCart) {
        queryClient.setQueryData(
          ["cart", cartToken, paymentMethodsIncludes],
          context?.previousCart,
        );
      }
    },
  });

  const removeDiscountMutation = useMutation({
    mutationKey: ["remove-discount", cartToken],
    mutationFn: (discountCode: string) =>
      removeDiscount(cartToken, discountCode),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ["cart", cartToken, paymentMethodsIncludes],
      });
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
      ]);
      queryClient.setQueryData<CartResponse>(
        ["cart", cartToken, paymentMethodsIncludes],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            cart: {
              ...old.cart,
              discount_code: null,
              discount_total_in_currency: "$0.00",
            },
          };
        },
      );
      return { previousCart };
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["cart", cartToken, paymentMethodsIncludes],
        response,
      );
    },
    onError: (error, _variables, context) => {
      console.error("Error removing discount:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(
          ["cart", cartToken, paymentMethodsIncludes],
          context.previousCart,
        );
      }
    },
  });

  const handleApplyDiscount = () => {
    applyDiscountMutation.mutate();
  };

  return (
    <>
      <div
        className="w-full cursor-pointer bg-gray-50 lg:hidden"
        onClick={() => setShowSummary(!showSummary)}
      >
        <div className="border-t border-b border-gray-200 px-8 py-4">
          <div className="mx-auto flex max-w-lg flex-row items-center justify-between">
            <div className="flex flex-row items-center space-x-2">
              <p className="text-sm font-semibold text-gray-900">
                {showSummary
                  ? t("hide_order_summary")
                  : t("show_order_summary")}
              </p>
              <FluidImage
                src={`${FLUID_CDN_URL}/images/checkout/icons/chevron-down.svg`}
                alt="Chevron Down"
                width={12}
                height={12}
                className={cn(
                  "translate-y-0.5 transform transition-transform duration-300 ease-in-out",
                  { "rotate-180": showSummary, "rotate-0": !showSummary },
                )}
              />
            </div>
            <div className="flex flex-row items-center space-x-2">
              <p className="text-base font-bold text-gray-900">
                {cart?.remaining_cart_amount_after_points_in_currency
                  ? cart?.remaining_cart_amount_after_points_in_currency
                  : cart?.amount_total_in_currency}
              </p>
            </div>
          </div>
        </div>
      </div>
      <section
        ref={scrollRef}
        aria-labelledby="summary-heading"
        className={cn(
          "flex w-full flex-col items-center bg-gray-50 lg:col-span-4 lg:overflow-y-auto lg:border-r lg:border-gray-200 lg:px-8 lg:[&::-webkit-scrollbar]:hidden",
          !inFrame && "lg:h-[94vh]",
        )}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <h2 id="summary-heading" className="sr-only">
          {t("order_summary")}
        </h2>
        <div className="flex w-full max-w-md flex-col px-8 md:px-0 lg:ml-auto">
          <div
            className={cn(
              "transition-all duration-500 ease-in-out",
              {
                "max-h-0 overflow-hidden": !showSummary,
              },
              "lg:max-h-none",
            )}
          >
            <DropZone
              zone="above_order_items"
              token={cartToken}
              subdomain={subdomain ?? null}
              page="checkout"
              companyId={cart?.company?.id}
            />
            {query.isLoading ? (
              <ProductLoader />
            ) : (
              <ProductList
                cart={cart || null}
                cartToken={cartToken}
                paymentMethodsIncludes={paymentMethodsIncludes}
                isSubscriptionPending={isSubscriptionPending}
                isQuantityPending={isQuantityPending}
              />
            )}
            <DropZone
              zone="below_order_items"
              token={cartToken}
              subdomain={subdomain ?? null}
              page="checkout"
              companyId={cart?.company?.id}
            />
            <DiscountCodeInput
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              onApplyDiscount={handleApplyDiscount}
              discountLoading={applyDiscountMutation.isPending}
              discountError={discountError}
              setDiscountError={setDiscountError}
              disabled={discountCode.length === 0 || isContactInfoPending}
            />
            <DropZone
              zone="below_discount_code"
              token={cartToken}
              subdomain={subdomain ?? null}
              page="checkout"
              companyId={cart?.company?.id}
            />
            <PricingSection
              query={query}
              discountLoading={applyDiscountMutation.isPending}
              onRemoveDiscount={removeDiscountMutation.mutate}
              deleteDiscountLoading={removeDiscountMutation.isPending}
            />
            <DropZone
              zone="below_total"
              token={cartToken}
              subdomain={subdomain ?? null}
              page="checkout"
              companyId={cart?.company?.id}
            />
          </div>
        </div>
      </section>
    </>
  );
}
